import { useState, useCallback, useRef } from 'react';
import type { FuelSavingInputField, FuelSavingFormComponentProps, FieldErrors, UseFieldValidationResult } from '../types';
import { isValidNumericInput } from '../utils';

/**
 * Hook for managing field validation state and handling validated input changes.
 * Shows red error border on invalid input that auto-clears after 1 second.
 * Prevents invalid values from being propagated to parent state, maintaining cursor position.
 * @param onValidChange - Callback invoked when input passes validation
 */
export const useFieldValidation = (onValidChange: FuelSavingFormComponentProps['onFieldChange']): UseFieldValidationResult => {
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
    const errorTimers = useRef<Partial<Record<FuelSavingInputField, ReturnType<typeof setTimeout>>>>({});

    const clearFieldError = useCallback((field: FuelSavingInputField) => {
        if (errorTimers.current[field]) {
            clearTimeout(errorTimers.current[field]);
            delete errorTimers.current[field];
        }
        setFieldErrors((prev) => ({ ...prev, [field]: false }));
    }, []);

    const showFieldError = useCallback(
        (field: FuelSavingInputField) => {
            if (errorTimers.current[field]) {
                clearTimeout(errorTimers.current[field]);
            }
            setFieldErrors((prev) => ({ ...prev, [field]: true }));
            errorTimers.current[field] = setTimeout(() => clearFieldError(field), 1000);
        },
        [clearFieldError]
    );

    const handleFieldChange = useCallback(
        (field: FuelSavingInputField, newValue: string) => {
            // Empty string is always valid
            if (newValue === '') {
                onValidChange(field, '');
                clearFieldError(field);
                return;
            }

            // Only propagate valid values to parent state
            if (isValidNumericInput(newValue)) {
                onValidChange(field, newValue);
                clearFieldError(field);
            } else {
                showFieldError(field);
            }
        },
        [onValidChange, clearFieldError, showFieldError]
    );

    return { fieldErrors, handleFieldChange };
};
